import React, { useEffect, useState,useRef } from "react";
import { ThirdWorkTreeAPI } from "../services/api";

interface WorkMeasureItem {
  workMeasureNo: number;
  workMeasure: string;
  objectNo?: number;
   parameter?: string;   // ADD
  value?: number;
}

interface Props {
  workNo: number | null;
}

const WorkMeasurePanel: React.FC<Props> = ({ workNo }) => {
  const [rows, setRows] = useState<WorkMeasureItem[]>([]);
  const [loading, setLoading] = useState(false);

  const [columnWidths, setColumnWidths] = useState({
  sno: 45,
  workmeasure: 150,
  value: 90,
  parameter: 150,
});

const [selectionStart, setSelectionStart] = useState<{
  row: number;
  column: string;
} | null>(null);

const [selectionEnd, setSelectionEnd] = useState<{
  row: number;
  column: string;
} | null>(null);

const [isSelecting, setIsSelecting] = useState(false);

const [selectAll, setSelectAll] = useState(false);

const [isCopied, setIsCopied] = useState(false);

const resizingRef = useRef<{
  column: keyof typeof columnWidths;
  startX: number;
  startWidth: number;
} | null>(null);

const allColumns = [
  "sno",
  "workmeasure",
  "value",
  "parameter",
];

  useEffect(() => {
    if (!workNo) {
      setRows([]);
      return;
    }

    setLoading(true);

    ThirdWorkTreeAPI.getWorkMeasures(workNo)
      .then(setRows)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [workNo]);

  // ADD THIS HERE
useEffect(() => {
  const stopSelecting = () => {
    setIsSelecting(false);
  };

  window.addEventListener("mouseup", stopSelecting);

  return () => {
    window.removeEventListener("mouseup", stopSelecting);
  };
}, []);

const isCellSelected = (
  rowIndex: number,
  columnKey: string
    
) => {
  if (!selectionStart || !selectionEnd) return false;

  const startRow = Math.min(
  selectionStart.row,
  selectionEnd.row
);

const endRow = Math.max(
  selectionStart.row,
  selectionEnd.row
);

const startCol = Math.min(
  allColumns.indexOf(selectionStart.column),
  allColumns.indexOf(selectionEnd.column)
);

const endCol = Math.max(
  allColumns.indexOf(selectionStart.column),
  allColumns.indexOf(selectionEnd.column)
);



  

  const currentCol =
    allColumns.indexOf(columnKey);

  return (
    rowIndex >= startRow &&
    rowIndex <= endRow &&
    currentCol >= startCol &&
    currentCol <= endCol
  );
};


const getSelectionBorderClass = (
  rowIndex: number,
  columnKey: string
) => {
  if (!selectionStart || !selectionEnd) return "";

  if (!isCellSelected(rowIndex, columnKey)) {
    return "";
  }

  const startRow = Math.min(selectionStart.row, selectionEnd.row);
  const endRow = Math.max(selectionStart.row, selectionEnd.row);

  const startCol = Math.min(
    allColumns.indexOf(selectionStart.column),
    allColumns.indexOf(selectionEnd.column)
  );

  const endCol = Math.max(
    allColumns.indexOf(selectionStart.column),
    allColumns.indexOf(selectionEnd.column)
  );

  const col = allColumns.indexOf(columnKey);

  let cls = "";

  if (rowIndex === startRow) cls += " border-t-[2px] border-t-green-600";
  if (rowIndex === endRow) cls += " border-b-[2px] border-b-green-600";
  if (col === startCol) cls += " border-l-[2px] border-l-green-600";
  if (col === endCol) cls += " border-r-[2px] border-r-green-600";

  return cls;
};

const startResize =
  (column: keyof typeof columnWidths) =>
  (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();

    resizingRef.current = {
      column,
      startX: e.clientX,
      startWidth: columnWidths[column],
    };

    const onMove = (ev: PointerEvent) => {
      if (!resizingRef.current) return;

      const width =
        resizingRef.current.startWidth +
        (ev.clientX - resizingRef.current.startX);

      setColumnWidths((prev) => ({
        ...prev,
        [column]: Math.max(60, width),
      }));
    };

    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      resizingRef.current = null;
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

const handleSelectAll = async () => {
  setSelectAll(true);

  setSelectionStart({
    row: 0,
    column: allColumns[0],
  });

  setSelectionEnd({
    row: rows.length - 1,
    column: allColumns[allColumns.length - 1],
  });

  const text = [
    [
      "S.No",
      "Work Measure",
      "Value",
      "Parameter",
    ].join("\t"),

    ...rows.map((row, index) =>
      [
        index + 1,
        row.workMeasure ?? "",
        row.value ?? "",
        row.parameter ?? "",
      ].join("\t")
    ),
  ].join("\n");

  await navigator.clipboard.writeText(text);
};

const copySelectedCells = async () => {
  if (!selectionStart || !selectionEnd) return;

  const startRow = Math.min(
    selectionStart.row,
    selectionEnd.row
  );

  const endRow = Math.max(
    selectionStart.row,
    selectionEnd.row
  );

  const startCol = Math.min(
    allColumns.indexOf(selectionStart.column),
    allColumns.indexOf(selectionEnd.column)
  );

  const endCol = Math.max(
    allColumns.indexOf(selectionStart.column),
    allColumns.indexOf(selectionEnd.column)
  );

  const text = [];

  for (let r = startRow; r <= endRow; r++) {
    const row = rows[r];

    const values = [];

    for (let c = startCol; c <= endCol; c++) {
      switch (allColumns[c]) {
        case "sno":
          values.push(String(r + 1));
          break;

        case "workmeasure":
          values.push(row.workMeasure ?? "");
          break;

        case "value":
          values.push(
            row.value != null
              ? String(row.value)
              : ""
          );
          break;

        case "parameter":
          values.push(row.parameter ?? "");
          break;
      }
    }

    text.push(values.join("\t"));
  }

  await navigator.clipboard.writeText(
    text.join("\n")
    
  );
  setIsCopied(true);
};

const handleKeyDown = (
  e: React.KeyboardEvent<HTMLDivElement>
) => {
  if (!selectionEnd) return;

  const currentRow = selectionEnd.row;

  const currentColIndex =
    allColumns.indexOf(selectionEnd.column);

  if (
    e.ctrlKey &&
    e.key.toLowerCase() === "c"
  ) {
    e.preventDefault();
   // handleSelectAll();
    copySelectedCells();
    return;
  }

  if (e.shiftKey) {
    e.preventDefault();

    switch (e.key) {
      case "ArrowDown":
        setSelectionEnd({
          row: Math.min(rows.length - 1, selectionEnd.row + 1),
          column: selectionEnd.column,
        });
        return;

      case "ArrowUp":
        setSelectionEnd({
          row: Math.max(0, selectionEnd.row - 1),
          column: selectionEnd.column,
        });
        return;

      case "ArrowRight":
        if (currentColIndex < allColumns.length - 1) {
          setSelectionEnd({
            row: selectionEnd.row,
            column: allColumns[currentColIndex + 1],
          });
        }
        return;

      case "ArrowLeft":
        if (currentColIndex > 0) {
          setSelectionEnd({
            row: selectionEnd.row,
            column: allColumns[currentColIndex - 1],
          });
        }
        return;
    }
  }

  switch (e.key) {
    case "ArrowDown":
      e.preventDefault();

      setSelectionStart({
        row: Math.min(rows.length - 1, currentRow + 1),
        column: selectionEnd.column,
      });

      setSelectionEnd({
        row: Math.min(rows.length - 1, currentRow + 1),
        column: selectionEnd.column,
      });
      break;

    case "ArrowUp":
      e.preventDefault();

      setSelectionStart({
        row: Math.max(0, currentRow - 1),
        column: selectionEnd.column,
      });

      setSelectionEnd({
        row: Math.max(0, currentRow - 1),
        column: selectionEnd.column,
      });
      break;

    case "ArrowRight":
      e.preventDefault();

      if (currentColIndex < allColumns.length - 1) {
        setSelectionStart({
          row: currentRow,
          column: allColumns[currentColIndex + 1],
        });

        setSelectionEnd({
          row: currentRow,
          column: allColumns[currentColIndex + 1],
        });
      }
      break;

    case "ArrowLeft":
      e.preventDefault();

      if (currentColIndex > 0) {
        setSelectionStart({
          row: currentRow,
          column: allColumns[currentColIndex - 1],
        });

        setSelectionEnd({
          row: currentRow,
          column: allColumns[currentColIndex - 1],
        });
      }
      break;

    case "Enter":
      e.preventDefault();

      setSelectionStart({
        row: Math.min(rows.length - 1, currentRow + 1),
        column: selectionEnd.column,
      });

      setSelectionEnd({
        row: Math.min(rows.length - 1, currentRow + 1),
        column: selectionEnd.column,
      });
      break;

    case "Tab":
      e.preventDefault();

      if (e.shiftKey) {
        if (currentColIndex > 0) {
          setSelectionStart({
            row: currentRow,
            column: allColumns[currentColIndex - 1],
          });

          setSelectionEnd({
            row: currentRow,
            column: allColumns[currentColIndex - 1],
          });
        }
      } else {
        if (currentColIndex < allColumns.length - 1) {
          setSelectionStart({
            row: currentRow,
            column: allColumns[currentColIndex + 1],
          });

          setSelectionEnd({
            row: currentRow,
            column: allColumns[currentColIndex + 1],
          });
        }
      }
      break;
  }
};

const autoFitColumn = (
  column: keyof typeof columnWidths
) => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;

  ctx.font = "13px Segoe UI";

  const headerMap = {
    sno: "S.No",
    workmeasure: "Work Measure",
    value: "Value",
    parameter: "Parameter",
  };

  let maxWidth = ctx.measureText(
    headerMap[column]
  ).width;

  rows.forEach((row, index) => {
    let value = "";

    switch (column) {
      case "sno":
        value = String(index + 1);
        break;

      case "workmeasure":
        value = row.workMeasure ?? "";
        break;

      case "value":
        value =
          row.value != null
            ? String(row.value)
            : "";
        break;

      case "parameter":
        value = row.parameter ?? "";
        break;
    }

    maxWidth = Math.max(
      maxWidth,
      ctx.measureText(value).width
    );
  });

  setColumnWidths((prev) => ({
    ...prev,
    [column]: Math.max(
      60,
      Math.ceil(maxWidth + 24)
    ),
  }));
};


  if (!workNo) {
    return (
      <div className="h-full flex items-center justify-center text-gray-500">
        Select a Work
      </div>
    );
  }

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        Loading...
      </div>
    );
  }

  return (
    /* <div
      className="h-full overflow-auto thin-scrollbar"
      style={{
        fontFamily: "'Segoe UI', sans-serif",
        fontSize: "13px",
      }}
    > */
     
  <div
    tabIndex={0}
    onKeyDown={handleKeyDown}
    onMouseDown={(e) => {
      const target = e.target as HTMLElement;

      if (!target.closest("table")) {
        setSelectionStart(null);
        setSelectionEnd(null);
        setSelectAll(false);
      }
    }}
    className="h-full overflow-auto thin-scrollbar outline-none"
    style={{
      fontFamily: "'Segoe UI', sans-serif",
      fontSize: "13px",
    }}
  >
      {rows.length === 0 ? (
        <div className="p-4 text-gray-500">
          No Work Measures found
        </div>
      ) : (
       
      //  <table className="w-full border-collapse">
      <table
  className="border-collapse select-none"
  style={{
    minWidth: "max-content",
  }}
>
  <colgroup>
  <col style={{ width: columnWidths.sno }} />
  <col style={{ width: columnWidths.workmeasure }} />
  <col style={{ width: columnWidths.value }} />
  <col style={{ width: columnWidths.parameter }} />
</colgroup>
          <thead
            style={{
              position: "sticky",
              top: 0,
              background: "#f3f4f6",
              zIndex: 10,
            }}
          >
            <tr>
              {/* <th className="border border-gray-300 px-2 py-1 text-left w-16">
                S.No
              </th> */}
              <th
//  className={`relative border border-gray-300 px-1 py-0 text-center cursor-pointer
className={`relative border border-gray-300 px-2 py-1 text-center cursor-pointer
   ${selectAll ? "bg-[#e6e6e6] border-t-[2px] border-l-[2px] border-green-600" : ""}
  `}
  style={{
  height: "26px",
  lineHeight: "20px",
}}
  onMouseDown={handleSelectAll}
>
  ◢

  <div
    onPointerDown={startResize("sno")}
    onDoubleClick={() => autoFitColumn("sno")}
    className="absolute right-0 top-0 h-full w-[4px] cursor-col-resize"
  />
</th>

             
               <th
 // className={`relative border border-gray-300 px-1 text-left
 className={`relative border border-gray-300 px-2 py-1 text-center cursor-pointer
   ${selectAll ? "bg-[#e6e6e6] border-t-[2px] border-r-[2px] border-green-600" : ""}
  `}
  style={{
    height: "26px",
    lineHeight: "20px",
  }}
> 

  Work Measure

  <div
    onPointerDown={startResize("workmeasure")}
    onDoubleClick={() => autoFitColumn("workmeasure")}
    className="absolute right-0 top-0 h-full w-[4px] cursor-col-resize"
  />
</th>

              {/* <th className="border border-gray-300 px-2 py-1 text-left">
                Value
              </th>
 */}

 <th
 // className={`relative border border-gray-300 px-1 text-left
 className={`relative border border-gray-300 px-2 py-1 text-left
   ${selectAll ? "bg-[#e6e6e6] border-t-[2px] border-r-[2px] border-green-600" : ""}
  `}
  style={{
    height: "26px",
    lineHeight: "20px",
  }}
>
  Value

  <div
    onPointerDown={startResize("value")}
    onDoubleClick={() => autoFitColumn("value")}
    className="absolute right-0 top-0 h-full w-[4px] cursor-col-resize"
  />
</th>
              {/* <th className="border border-gray-300 px-2 py-1 text-left">
              Parameter
              </th> */}
              <th
  //className={`relative border border-gray-300 px-1 text-left
  className={`relative border border-gray-300 px-2 py-1 text-left
   ${selectAll ? "bg-[#e6e6e6] border-t-[2px] border-r-[2px] border-green-600" : ""}
  `}
  style={{
    height: "26px",
    lineHeight: "20px",
  }}
>
  Parameter

  <div
    onPointerDown={startResize("parameter")}
    onDoubleClick={() => autoFitColumn("parameter")}
    className="absolute right-0 top-0 h-full w-[4px] cursor-col-resize"
  />
</th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row, index) => (
              <tr
                key={row.workMeasureNo}
                className="hover:bg-blue-50"
              >
               {/*  <td className="border border-gray-300 px-2 py-1">
                  {index + 1}
                </td> */}
                <td
  onMouseDown={() => {
    setSelectAll(false);
    setIsCopied(false);


    setSelectionStart({
      row: index,
      column: "sno",
    });

    setSelectionEnd({
      row: index,
      column: "sno",
    });

    setIsSelecting(true);
  }}
  onMouseEnter={() => {
    if (!isSelecting) return;

    setSelectionEnd({
      row: index,
      column: "sno",
    });
  }}
  /* className={`border border-gray-300 px-1 py-0 cursor-pointer whitespace-nowrap overflow-hidden text-clip
    ${isCellSelected(index, "sno") ? "bg-[#cfe8ff]" : ""}
  `} */
 className={`border border-gray-300 px-2 py-1 h-[26px] leading-[20px] cursor-pointer whitespace-nowrap overflow-hidden text-clip
    ${isCellSelected(index, "sno") ? "bg-[#e6e6e6]" : ""}
    ${getSelectionBorderClass(index, "sno")}
    ${isCopied && isCellSelected(index, "sno") ? "copy-selection" : ""}
`}
>
  {index + 1}
</td>

               {/*  <td className="border border-gray-300 px-2 py-1">
                  {row.workMeasure}
                </td> */}
                <td
  onMouseDown={() => {
    setSelectAll(false);
    setIsCopied(false);


    setSelectionStart({
      row: index,
      column: "workmeasure",
    });

    setSelectionEnd({
      row: index,
      column: "workmeasure",
    });

    setIsSelecting(true);
  }}
  onMouseEnter={() => {
    if (!isSelecting) return;

    setSelectionEnd({
      row: index,
      column: "workmeasure",
    });
  }}
 className={`border border-gray-300 px-2 py-1 h-[26px] leading-[20px] cursor-pointer whitespace-nowrap overflow-hidden text-clip
    
   ${isCellSelected(index, "workmeasure") ? "bg-[#e6e6e6]" : ""}
${getSelectionBorderClass(index, "workmeasure")}
${isCopied && isCellSelected(index, "workmeasure") ? "copy-selection" : ""}
 
 `}
>
  {row.workMeasure}
</td>

                {/* <td className="border border-gray-300 px-2 py-1 text-right">
                  {row.value ?? ""}
                </td> */}

                <td
  onMouseDown={() => {
    setSelectAll(false);
    setIsCopied(false);


    setSelectionStart({
      row: index,
      column: "value",
    });

    setSelectionEnd({
      row: index,
      column: "value",
    });

    setIsSelecting(true);
  }}
  onMouseEnter={() => {
    if (!isSelecting) return;

    setSelectionEnd({
      row: index,
      column: "value",
    });
  }}
  className={`border border-gray-300 px-1 py-0 text-right cursor-pointer whitespace-nowrap overflow-hidden text-clip
   ${isCellSelected(index, "value") ? "bg-[#e6e6e6]" : ""}
${getSelectionBorderClass(index, "value")}
${isCopied && isCellSelected(index, "value") ? "copy-selection" : ""}
  `}
>
  {row.value ?? ""}
</td>

               {/*  <td className="border border-gray-300 px-2 py-1">
                 {row.parameter ?? ""}
                </td> */}
                <td
  onMouseDown={() => {
    setSelectAll(false);
    setIsCopied(false);


    setSelectionStart({
      row: index,
      column: "parameter",
    });

    setSelectionEnd({
      row: index,
      column: "parameter",
    });

    setIsSelecting(true);
  }}
  onMouseEnter={() => {
    if (!isSelecting) return;

    setSelectionEnd({
      row: index,
      column: "parameter",
    });
  }}
 className={`border border-gray-300 px-2 py-1 h-[26px] leading-[20px] cursor-pointer whitespace-nowrap overflow-hidden text-clip
    ${isCellSelected(index, "parameter") ? "bg-[#e6e6e6]" : ""}
${getSelectionBorderClass(index, "parameter")}
${isCopied && isCellSelected(index, "parameter") ? "copy-selection" : ""}
  `}
>
  {row.parameter ?? ""}
</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default WorkMeasurePanel;